import { useState, useEffect } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import brandService from '../../services/brandService';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import Table from '../../components/Common/Table';
import CreateBrandDialog from './CreateBrandDialog';

const BrandsIndex = () => {
  const { get, isRtl } = useLocalization();
  const [brands, setBrands] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    try {
      setIsLoading(true);
      const data = await brandService.getAll();
      setBrands(data);
    } catch (error) {
      console.error('Error loading brands:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(isRtl ? 'هل أنت متأكد من حذف هذه العلامة التجارية؟' : 'Are you sure you want to delete this brand?')) {
      try {
        await brandService.delete(id);
        loadBrands();
      } catch (error) {
        console.error('Error deleting brand:', error);
      }
    }
  };

  const handleToggleVisibility = async (brand) => {
    const nextVisible = !brand.isActive;
    const msg = nextVisible
      ? (isRtl ? 'إظهار جميع عطور هذه العلامة في المتجر؟' : 'Show all perfumes from this brand on the store?')
      : (isRtl ? 'إخفاء جميع عطور هذه العلامة من المتجر؟' : 'Hide all perfumes from this brand on the store?');
    if (!window.confirm(msg)) return;
    try {
      await brandService.setStoreVisibility(brand.id, nextVisible);
      loadBrands();
    } catch (error) {
      console.error('Error updating brand visibility:', error);
      alert(isRtl ? 'تعذر تحديث الحالة' : 'Failed to update visibility');
    }
  };

  const filteredBrands = brands.filter(brand => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      brand.nameEn?.toLowerCase().includes(search) ||
      brand.nameAr?.toLowerCase().includes(search)
    );
  });

  const columns = [
    { header: isRtl ? 'الاسم (إنجليزي)' : 'Name (English)', accessor: 'nameEn' },
    { header: isRtl ? 'الاسم (عربي)' : 'Name (Arabic)', accessor: 'nameAr' },
    {
      header: isRtl ? 'عدد المنتجات' : 'Products',
      accessor: 'productCount',
      render: (value) => value || 0,
    },
    {
      header: isRtl ? 'الحالة' : 'Status',
      accessor: 'isActive',
      render: (value) => (
        <span className={`badge ${value ? 'badge-success' : 'badge-warning'}`}>
          {value ? (isRtl ? 'ظاهر في المتجر' : 'Visible on store') : (isRtl ? 'مخفي من المتجر' : 'Hidden from store')}
        </span>
      ),
    },
    {
      header: isRtl ? 'الإجراءات' : 'Actions',
      accessor: 'id',
      render: (value, row) => (
        <div className="action-buttons">
          <Button
            size="small"
            variant={row.isActive ? 'secondary' : 'primary'}
            onClick={() => handleToggleVisibility(row)}
          >
            {row.isActive ? (isRtl ? 'إخفاء من المتجر' : 'Hide from store') : (isRtl ? 'إظهار في المتجر' : 'Show on store')}
          </Button>
          <Button size="small" variant="danger" onClick={() => handleDelete(value)}>
            {get('delete')}
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text={isRtl ? 'جاري التحميل...' : 'Loading brands...'} />;
  }

  return (
    <div className="brands-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{isRtl ? 'إدارة العلامات التجارية' : 'Brand Management'}</h1>
            <p className="page-description">{isRtl ? 'إدارة مجموعة علامات العطور التجارية' : 'Manage your perfume brand collection'}</p>
          </div>
          <div className="page-header-actions">
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              {get('add_new_brand')}
            </Button>
          </div>
        </div>
      </div>

      <div className="stats-grid mb-6">
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                <line x1="7" y1="7" x2="7.01" y2="7"></line>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{brands.length}</div>
              <div className="stat-label">{isRtl ? 'إجمالي العلامات التجارية' : 'Total Brands'}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-success">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{brands.filter(b => b.isActive).length}</div>
              <div className="stat-label">{isRtl ? 'علامات نشطة' : 'Active Brands'}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-info">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{brands.reduce((sum, b) => sum + (b.productCount || 0), 0)}</div>
              <div className="stat-label">{isRtl ? 'إجمالي المنتجات' : 'Total Products'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="search-box">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder={isRtl ? 'بحث عن العلامات التجارية...' : 'Search brands...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <span className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>
            {isRtl ? `${filteredBrands.length} علامة` : `${filteredBrands.length} brands`}
          </span>
        </div>
        <div className="card-body p-0">
          <Table
            columns={columns}
            data={filteredBrands}
            emptyMessage={isRtl ? 'لا توجد علامات تجارية' : 'No brands found'}
          />
        </div>
      </div>

      {showCreateModal && (
        <CreateBrandDialog
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadBrands();
          }}
        />
      )}
    </div>
  );
};

export default BrandsIndex;
